import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { getListingCityStateString } from 'common/listingHelpers';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';
import ListingActions from 'client/actions/Listing';
import branch from 'client/core/branch';

import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';

const FUZZY_SEARCH_CHAR_DELAY_MS = 100;

@branch({
  sunlightSearch: ['fuzzySearchListings'],
})
class SrListingSearchWidget extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    sunlightSearch: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      searchInput: '',
    };
  }

  _pressedKeyCombination = (e) => {
    const isMac = /Mac/.test(navigator.userAgent);
    return e.key === 's' && (isMac ? e.metaKey : e.ctrlKey);
  };

  componentDidMount() {
    if (!global.document) {
      return;
    }

    this.pollInterval = setInterval(this._checkSearchInput, 500);

    global.document.addEventListener(
      'keydown',
      (e) => {
        const pressedKeyCombination = this._pressedKeyCombination(e);
        if (pressedKeyCombination) {
          e.preventDefault();
          this.setState({
            isOpen: !this.state.isOpen,
          });
          if (this._searchInputEl) {
            this._searchInputEl.focus();
          }
        }
      },
      false,
    );
  }

  componentWillUnmount() {
    clearInterval(this.pollInterval);
  }

  _checkSearchInput = () => {
    const { searchInput, isOpen } = this.state;
    const { sunlightSearch } = this.props;

    if (!isOpen || searchInput.length === 0) {
      return;
    }

    if (searchInput !== sunlightSearch.latestSearchTermRetrieved) {
      this._search();
    }
  };

  _onInputChange = (e) => {
    if (!process.browser) {
      return;
    }
    const input = e.target.value || '';

    this.setState({
      searchInput: input,
    });

    if (input.length === 0) {
      return;
    }

    clearTimeout(this.timeout);
    this.timeout = setTimeout(this._search, FUZZY_SEARCH_CHAR_DELAY_MS);
  };

  _search = () => {
    const { searchInput } = this.state;
    const { dispatch } = this.props;

    dispatch(ListingActions.loadFuzzySearchListings, {
      searchTerm: searchInput || '',
    });
  };

  _closeSearch = () => {
    this.setState({
      isOpen: false,
    });
  };

  render() {
    const { isOpen, searchInput } = this.state;
    const { currentUser, sunlightSearch } = this.props;
    const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);
    const {
      results: { listings = [], partners = [] },
    } = sunlightSearch || {};

    if (!isOpen) {
      return null;
    }
    return (
      <div onClick={this._closeSearch} className="SrCenteredWidgetContainer">
        <div onClick={preventDefaultPropagation} className="SrListingSearchWidget">
          <div className="header">
            <input
              ref={(el) => {
                this._searchInputEl = el;
              }}
              value={searchInput}
              type="text"
              onChange={this._onInputChange}
              placeholder="Sunlight search"
            />
          </div>
          <div className="content">
            {listings.length > 0 && (
              <div className="contentGroupHeading">
                <SrText font="whitney-sc">listings</SrText>
              </div>
            )}
            {listings.map((listing) => (
              <SrLink
                className="resultRowContainer listing"
                onClick={this._closeSearch}
                to={`/partner/${listing.partnerSlug}/listing/${listing.id}`}
              >
                <SrFlex className="resultRow" key={listing.id}>
                  <div className="resultName listing">
                    <SrText className="addressStreet">{`${listing.addressStreetDisplay}`}</SrText>
                    <SrText className="addressCityState">
                      {` ${getListingCityStateString(listing)}`}
                    </SrText>
                  </div>
                  <div className="resultInfo">
                    <SrFlex>
                      <SrText className="infoCell">{listing.status}</SrText>
                      <SrText className="infoCell">
                        {listing.listingManagementCompany
                          ? listing.listingManagementCompany
                          : 'RealSavvy'}
                      </SrText>
                      <SrText className="infoCell">{`MLS# ${listing.mlsNumber || '--'}`}</SrText>
                      {isAdmin && (
                        <SrText className="infoCell link">
                          <SrLink to={`/admin/listings/edit/${listing.id}`}>Edit</SrLink>
                        </SrText>
                      )}
                    </SrFlex>
                  </div>
                </SrFlex>
              </SrLink>
            ))}
            {isAdmin && (
              <>
                {partners.length > 0 && (
                  <div className="contentGroupHeading">
                    <SrText font="whitney-sc">partners</SrText>
                  </div>
                )}
                {partners.map((partner) => (
                  <SrLink
                    className="resultRowContainer partner"
                    onClick={this._closeSearch}
                    to={`/partner/${partner.slug}`}
                  >
                    <SrFlex className="resultRow" key={partner.id}>
                      <div className="resultName partner">
                        <SrText className="displayName">{partner.displayName} &nbsp;</SrText>
                        <SrText className="slug">{partner.slug}</SrText>
                      </div>
                      <div className="resultInfo">
                        <SrFlex>
                          <SrText className="infoCell link">
                            <SrLink to={`/partner/${partner.slug}`}>Go to Partner Portal</SrLink>
                          </SrText>
                        </SrFlex>
                      </div>
                    </SrFlex>
                  </SrLink>
                ))}
              </>
            )}
          </div>
          <div className="footer">
            {isAdmin && (
              <SrLink onClick={this._closeSearch} to="/admin/listings">
                View all Sunroom listings →
              </SrLink>
            )}
          </div>
        </div>
      </div>
    );
  }
}

SrListingSearchWidget.displayName = 'SrListingSearchWidget';

export default SrListingSearchWidget;
